import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  IconButton,
  TextField,
  Card,
  CardContent,
  Box,
  Typography,
  Stack,
  Fade,
  CircularProgress,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { MessageSquare, X, Send, Minimize2 } from "lucide-react";
import axios from "axios";
import { endpoint } from "../../resource/Constant";
import { useNavigate } from "react-router-dom";
import { use } from "marked";

const DEFAULT_HEADER_GRADIENT = "linear-gradient(90deg, #1976d2, #42a5f5, #66bb6a)";
const DEFAULT_USER_GRADIENT = "linear-gradient(135deg, #1976d2, #42a5f5, #66bb6a)";
const THEME_EVENT = "crawling-theme-updated";

interface Message {
  id: string;
  role: "user" | "bot";
  content: string;
  timestamp: Date;
  isLoader? : boolean;
}

export const ChatWidget = () => {
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [headerGradient, setHeaderGradient] = useState<string>(DEFAULT_HEADER_GRADIENT);
  const [userGradient, setUserGradient] = useState<string>(DEFAULT_USER_GRADIENT);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: "1",
      role: "bot",
      content: "Hi! I'm here to help you with any questions about the website. What would you like to know?",
      timestamp: new Date(),
    },
  ]);
  const [input, setInput] = useState("");
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  const buildGradient = (angle, colors) =>
    `linear-gradient(${angle}deg, ${colors})`;

  useEffect(() => {
  messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Load gradients selected in Theme Customizer (persisted in localStorage)
  useEffect(() => {
    if (localStorage.getItem("crawling-gradient-header") || localStorage.getItem("crawling-gradient-bubble")) {
      const loadGradients = () => {
        const storedHeader = localStorage.getItem("crawling-gradient-header");
        const storedBubble = localStorage.getItem("crawling-gradient-bubble");
        if (storedHeader) setHeaderGradient(storedHeader);
        if (storedBubble) setUserGradient(storedBubble);
      };
      loadGradients();
      // Update if localStorage changes in other tabs
      window.addEventListener("storage", loadGradients);
      // Update live in this tab via custom event
      const handleThemeEvent = (e: Event) => {
        const detail = (e as CustomEvent)?.detail;
        if (!detail) return;
        if (detail.headerGradient) setHeaderGradient(detail.headerGradient);
        if (detail.bubbleGradient) setUserGradient(detail.bubbleGradient);
      };
      window.addEventListener(THEME_EVENT, handleThemeEvent);
      return () => {
        window.removeEventListener("storage", loadGradients);
        window.removeEventListener(THEME_EVENT, handleThemeEvent);
      };
    }
    else {
      axios.get(`${endpoint}/api/crawling/get-color-private` , {headers : {Authorization : `Bearer ${localStorage.getItem("crawling")}`}})
      .then((response) => {
        if (response?.data?.data?.header_gradient) {
          const header_gradient = response?.data?.data?.header_gradient;
          setHeaderGradient(buildGradient(header_gradient.angle, header_gradient.color))

        }
        if (response?.data?.data?.user_gradient) {
          const user_gradient = response?.data?.data?.user_gradient;
          setUserGradient(buildGradient(user_gradient.angle, user_gradient.color))
        }

      })
      .catch((error) => {
        const status = error?.response?.status;
        if (status === 401 || status === 403) {
          localStorage.removeItem("crawling");
          navigate('/crawling/signin');
          return;
        }
      })
    }
  }, []);

  
  const { enqueueSnackbar } = useSnackbar();
  
  const showSnackbar = (message: string, severity: "success" | "error" | "info") => {
        enqueueSnackbar(message, { variant: severity });
  };


  const handleSend = async () => {
    if (!input.trim()) return;
    const messageText = input;
    setInput("");


    const userMessage: Message = {
      id: Date.now().toString(),
      role: "user",
      content: messageText,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);

      // 3. Add loader message
    const loaderMessage: Message = {
      id: "loader",
      role: "bot",
      content: "",    // replace this with your animation component
      isLoader: true,          // custom flag
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, loaderMessage]);

      try {
      // 4. Call backend
      let response;
      try {
        const api_response = await axios.post(`${endpoint}/api/crawling/chat`, 
          {query: messageText},
          {headers : {
          Authorization : `Bearer ${localStorage.getItem('crawling')}`
        }}
        );
        response = api_response
      } catch (error) {
        showSnackbar(
          error?.response?.data?.detail ||
            error?.response?.data?.message ||
            "Internal Server Error",
          "error"
        );
        if (error?.response?.status === 401 || error?.response?.status === 403) {
          localStorage.removeItem('crawling')
          navigate('/crawling/signin')
          return;
        }
      }

      const botMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: "bot",
        content: response?.data?.data?.answer || "No response",
        timestamp: new Date(),
      };

      // 5. Remove loader and add bot message
      setMessages((prev) =>
        prev.filter((msg) => msg.id !== "loader").concat(botMessage)
      );

    } catch (error) {
      // Remove loader and show error
      setMessages((prev) =>
        prev.filter((msg) => msg.id !== "loader").concat({
          id: (Date.now() + 2).toString(),
          role: "bot",
          content: "Error: Unable to reach server.",
          timestamp: new Date(),
        })
      );
    }
  };

  if (!isOpen) {
    return (
      <Button
        onClick={() => setIsOpen(true)}
        variant="contained"
        sx={{
          position: "fixed",
          bottom: 24,
          right: 24,
          minWidth: 64,
          height: 64,
          width: 64,
          borderRadius: "50%",
          boxShadow: 6,
          zIndex: 1300,
          background: userGradient,
          "&:hover": {
            background: userGradient,
            transform: "scale(1.1)",
            boxShadow: 8,
          },
          transition: "all 0.3s ease",
        }}
      >
        <MessageSquare size={28} />
      </Button>
    );
  }

  return (
    <Box
      sx={{
        position: "fixed",
        bottom: 24,
        right: 24,
        zIndex: 1300,
      }}
    >
      <Card
        sx={{
          width: 384,
          height: isMinimized ? 64 : 600,
          transition: "all 0.3s ease",
          borderRadius : 6,
          overflow: "hidden",
          boxShadow: 8,
          transformOrigin: "bottom right",
          background: "linear-gradient(135deg, rgba(255,255,255,0.95), rgba(255,255,255,0.9), rgba(255,255,255,0.95))",
          border: "2px solid rgba(25, 118, 210, 0.2)",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 2,
            borderBottom: "1px solid rgba(25, 118, 210, 0.2)",
            background: headerGradient,
            overflow: "hidden",
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ position: "relative", zIndex: 1 }}>
            <Box
              sx={{
                p: 1,
                borderRadius: "50%",
                bgcolor: "rgba(255,255,255,0.2)",
                backdropFilter: "blur(10px)",
              }}
            >
              <MessageSquare size={25} color="white" />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: "bold", color: "white", fontSize: "1.125rem" }}>
                AI Assistant
              </Typography>
              <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.8)", fontSize: "0.75rem" }}>
                Always here to help
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={0.5} sx={{ position: "relative", zIndex: 1 }}>
            <IconButton
              size="small"
              onClick={() => setIsMinimized(!isMinimized)}
              sx={{
                color: "white",
                "&:hover": {
                  bgcolor: "rgba(255,255,255,0.2)",
                  transform: "scale(1.1)",
                },
                transition: "all 0.2s ease",
              }}
            >
              <Minimize2 size={16} />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => setIsOpen(false)}
              sx={{
                color: "white",
                "&:hover": {
                  bgcolor: "rgba(255,255,255,0.2)",
                  transform: "scale(1.1)",
                },
                transition: "all 0.2s ease",
              }}
            >
              <X size={16} />
            </IconButton>
          </Stack>
        </Box>

        {!isMinimized && (
          <>
            {/* Messages */}
            <Box
              sx={{
                flex: 1,
                overflowY: "auto",
                p: 2,
                height: 450,
                background: "linear-gradient(180deg, rgba(0,0,0,0.02), rgba(0,0,0,0.05))",
              }}
            >
              <Stack spacing={2}>
                {messages.map((message) => (
                  <Fade in key={message.id} timeout={500}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: message.role === "user" ? "flex-end" : "flex-start",
                      }}
                    >
                      <Box
                        sx={{
                          maxWidth: "85%",
                          borderRadius: 4,
                          px: 2.5,
                          py: 1.5,
                          boxShadow: 3,
                          ...(message.role === "user"
                            ? {
                                background: headerGradient,
                                color: "white",
                                borderTopRightRadius: 4,
                                borderBottomRightRadius: 8,
                              }
                            : {
                                bgcolor: "background.paper",
                                color: "text.primary",
                                border: "1px solid rgba(25, 118, 210, 0.1)",
                                borderTopLeftRadius: 4,
                                borderBottomLeftRadius: 8,
                              }),
                        }}
                      >
                        {/* Loader */}
                        {message.isLoader ? (
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            {[0, 1, 2].map((i) => (
                              <Box
                                key={i}
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: "50%",
                                  bgcolor: "rgba(0,0,0,0.3)",
                                  animation: "bounce 1.4s infinite",
                                  animationDelay: `${i * 0.15}s`,
                                  "@keyframes bounce": {
                                    "0%, 80%, 100%": { transform: "translateY(0)" },
                                    "40%": { transform: "translateY(-10px)" },
                                  },
                                }}
                              />
                            ))}
                          </Stack>
                        ) : (
                          <>
                            <Typography variant="body2" sx={{ lineHeight: 1.75, color: message.role === "user" ? "white" : "inherit" }}>
                              {message.content}
                            </Typography>
                            {/* Time */}
                            <Typography
                              variant="caption"
                              sx={{
                                display: "block",
                                opacity: 0.6,
                                mt: 0.75,
                                fontWeight: 500,
                                color: message.role === "user" ? "rgba(255,255,255,0.8)" : "inherit",
                              }}
                            >
                              {message.timestamp.toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </Typography>
                          </>
                        )}
                      </Box>
                    </Box>
                  </Fade>
                ))}
              </Stack>
              {/* Auto-scroll anchor */}
              <div ref={messagesEndRef} />
            </Box>

            {/* Input */}
            <Box
              sx={{
                p: 1,
                borderTop: "1px solid rgba(25, 118, 210, 0.1)",
                bgcolor: "rgba(0,0,0,0.02)",
              }}
            >
              <Stack direction="row" spacing={1}>
                <TextField
                  autoComplete="off"
                  fullWidth
                  size="small"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask me anything..."
                  onKeyDown={(e) => e.key === "Enter" && handleSend()}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 3,
                      bgcolor: "rgba(0,0,0,0.02)",
                      "& fieldset": {
                        borderColor: "rgba(25, 118, 210, 0.2)",
                      },
                      "&:hover fieldset": {
                        borderColor: "rgba(25, 118, 210, 0.3)",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "rgba(25, 118, 210, 0.5)",
                      },
                    },
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleSend}
                  disabled={!input.trim()}
                  sx={{
                    background: userGradient,
                    borderRadius: 3,
                    px: 2.5,
                    minWidth: 48,
                    "&:hover": {
                      background: userGradient,
                      transform: "scale(1.05)",
                      boxShadow: 6,
                    },
                    "&:disabled": {
                      opacity: 0.5,
                    },
                    transition: "all 0.2s ease",
                  }}
                >
                  <Send size={16} />
                </Button>
              </Stack>
            </Box>
          </>
        )}
      </Card>
    </Box>
  );
};
